import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { APIS } from './serviceUrls';
import {
  getAccessToken,
  getRefreshToken,
  setSession,
  clearSession,
} from './jwt';
import { PATH_AUTH } from '../routes/paths';
import { AuthNotice, setAuthNotice } from './authNotice';

// Staff sign-in code (auth/staff-otp). A staff session lasts at most a day
// from its code and is never extended by a refresh; once it is over (or for a
// session opened before codes existed) the API answers 401 with this code on
// every call AND on refresh-token. Refreshing cannot help, so the app signs
// out straight away instead of trying.
export const STAFF_CODE_REQUIRED = 'STAFF_CODE_REQUIRED';
// A new staff account must choose its own password first: 403 with this code
// on everything except change-password, me and logout.
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED';
// Fired on window when a call is refused with PASSWORD_CHANGE_REQUIRED; the
// AuthProvider listens and routes the person to the change-password screen.
export const PASSWORD_CHANGE_REQUIRED_EVENT = 'kayfa:password-change-required';

const errorCode = (data: unknown): string | undefined =>
  data && typeof data === 'object' ? (data as any).code : undefined;

// ----------------------------------------------------------------------
// Phase 2.1 axios instance with silent 401-refresh.
//
// On any 401 response, the interceptor:
//   1. Attempts a single POST /v1/auth/refresh-token using the stored
//      refresh token. Concurrent 401s share one in-flight refresh —
//      they all queue behind `refreshPromise` and retry once it resolves.
//   2. On refresh success, persists the new tokens, replays the failed
//      request with the new Authorization header.
//   3. On refresh failure, clears the session and redirects to /auth/login.
//
// No `alert('Token expired')`. No silent loss of in-flight work — the
// failed request is retried after refresh, transparent to the caller.
// ----------------------------------------------------------------------

const axiosInstance = axios.create();
axiosInstance.defaults.headers.common['Content-Type'] = 'application/json';

// Attach Bearer header from storage on every request. This also catches
// requests issued before login finished initializing the default header.
axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && config.headers) {
    // axios v0/v1 header types differ; cast to any to satisfy both.
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;
let isRedirecting = false;

const performRefresh = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    // Use a bare axios call (not axiosInstance) so we don't recurse
    // through the 401 interceptor if the refresh itself returns 401.
    const response = await axios.post(APIS.AUTH.REFRESH, { refreshToken });
    const data = response.data?.data ?? response.data;
    const newAccess: string | undefined =
      data?.accessToken ?? data?.payload?.accessToken;
    const newRefresh: string | undefined = data?.refreshToken;
    if (!newAccess) return null;
    setSession(newAccess, newRefresh ?? undefined);
    return newAccess;
  } catch (err: any) {
    // The staff day is over: say so on the login page.
    if (errorCode(err?.response?.data) === STAFF_CODE_REQUIRED) {
      setAuthNotice('session_ended');
    }
    return null;
  }
};

const redirectToLogin = (notice?: AuthNotice): void => {
  clearSession();
  if (notice) setAuthNotice(notice);
  if (isRedirecting) return;
  if (typeof window === 'undefined') return;
  // Already on the login page (e.g. a stored session was refused while the
  // app initialised): the AuthProvider drops the user itself, so reloading
  // the same page would only flash it.
  if (window.location.pathname === PATH_AUTH.login) return;
  isRedirecting = true;
  window.location.href = PATH_AUTH.login;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest =
      (error.config as AxiosRequestConfig & { _retry?: boolean }) ?? {};
    const status = error.response?.status;

    // Don't run the silent-refresh + redirect dance on the auth endpoints.
    // A 401 from sign-in is an expected bad-password / bad-MFA-code response,
    // and a 401 from refresh-token is already terminal (no valid refresh
    // token). Both must propagate to the caller (the login + MFA forms render
    // an inline error) instead of triggering a hard redirect to /auth/login,
    // which manifests as a full page reload.
    const isAuthCall =
      typeof originalRequest.url === 'string' &&
      (originalRequest.url.includes('/auth/sign-in') ||
        originalRequest.url.includes('/auth/verify-otp') ||
        originalRequest.url.includes('/auth/refresh-token'));
    const code = errorCode(error.response?.data);

    // The staff session needs a new code: no refresh attempt (the API refuses
    // it with the same code), no retry; sign out and show the login note.
    if (status === 401 && code === STAFF_CODE_REQUIRED && !isAuthCall) {
      redirectToLogin('session_ended');
      return Promise.reject(error.response?.data);
    }

    if (status === 403 && code === PASSWORD_CHANGE_REQUIRED && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PASSWORD_CHANGE_REQUIRED_EVENT));
    }

    if (status === 401 && !originalRequest._retry && !isAuthCall) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = performRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccess = await refreshPromise;
        if (!newAccess) {
          redirectToLogin();
          // The @typescript-eslint/return-await rule requires us to await
          // here so the rejection is observable inside this try block; the
          // catch below maps the same error shape used elsewhere.
          // eslint-disable-next-line no-return-await
          return await Promise.reject(
            (error.response && error.response.data) || 'Session expired',
          );
        }
        if (originalRequest.headers) {
          (originalRequest.headers as any).Authorization = `Bearer ${newAccess}`;
        }
        // eslint-disable-next-line no-return-await
        return await axiosInstance(originalRequest);
      } catch (refreshErr) {
        redirectToLogin();
        return Promise.reject(refreshErr);
      }
    }

    if (status === 401 && !isAuthCall) {
      // Either a retry that still failed, or the refresh endpoint itself
      // refused us. Either way, the session is gone.
      redirectToLogin();
    }

    return Promise.reject(
      (error.response && error.response.data) || 'Something went wrong',
    );
  },
);

export default axiosInstance;
